import Table from "@shared/components/Table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import JobDescriptionForm from "./JobDescriptionForm";
import { companiesService } from "@shared/services/companiesService";
import { jobDescriptionsService } from "@modules/organizationalStructure/jobDescriptions/services/jobDescriptions";
import DeleteModal from "@shared/components/DeleteModal";
import { notifySuccess } from "@config/tostifyConfig/tostifyConfig";
import { COMMON_BUTTONS } from "@config/actionButtonConfigs";
import ActionButtons from "@shared/components/ActionButtons";
import useOpenFiles from "@shared/hooks/useOpenFiles";
import { getFileIcon } from "@utils/fileIcons";
import {
    extractErrorMessage,
    shouldBlockFetchError,
    showBlockingError,
} from '@shared/services/blockingError';

const JOB_DESCRIPTIONS_FETCH_OPTIONS = { blockingError: true };

function handleJobDescriptionsAccessError(error) {
    if (shouldBlockFetchError(error, JOB_DESCRIPTIONS_FETCH_OPTIONS)) {
        showBlockingError(extractErrorMessage(error));
        return true;
    }

    return false;
}

export default function JobDescriptions() {
    const { t } = useTranslation();
    const openJobDescriptionFile = useOpenFiles('/hr-job-description/file');

    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [modalState, setModalState] = useState({ visible: false, type: null, editData: null });
    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(true);
    const [selectedActive, setSelectedActive] = useState('true'); // Default to active
    const [jobDescriptions, setJobDescriptions] = useState([]);
    const [jobDescriptionsLoading, setJobDescriptionsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState({ fileName: '', description: '' });
    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                setCompaniesLoading(true);
                const response = await companiesService.getAllCompanies();
                setCompanies(response);
                if (response && response.length > 0) {
                    setSelectedCompany(response[0].id);
                }
            } catch (error) {
                console.error('Error fetching companies:', error);
                if (!handleJobDescriptionsAccessError(error)) {
                    setError('Failed to load companies');
                } else {
                    setError(null);
                }
            } finally {
                setCompaniesLoading(false);
            }
        }

        fetchCompanies();
    }, []);

    useEffect(() => {
        const fetchJobDescriptions = async () => {
            if (selectedCompany) {
                try {
                    setJobDescriptionsLoading(true);
                    setError(null);
                    
                    // Build search params with active filter
                    const searchParams = {};
                    if (selectedActive !== null && selectedActive !== '') {
                        searchParams.active = selectedActive === 'true';
                    }
                    
                    const response = await jobDescriptionsService.getJobDescriptions(selectedCompany, searchParams);
                    setJobDescriptions(response.content || []);
                } catch (error) {
                    console.error('❌ [useEffect] Error fetching job descriptions:', error);
                    setJobDescriptions([]);
                    if (!handleJobDescriptionsAccessError(error)) {
                        setError('Failed to load job descriptions');
                    } else {
                        setError(null);
                    }
                } finally {
                    setJobDescriptionsLoading(false);
                }
            }
        }

        fetchJobDescriptions();
    }, [selectedCompany, selectedActive]);




    const columns = [
        {
            key: "fileName",
            label: t('organizationalStructure.jobDescriptions.table.headers.jobDescriptions')
        },
        {
            key: "description",
            label: t('organizationalStructure.jobDescriptions.table.headers.description')
        },
        {
            key: "status",
            label: t('organizationalStructure.jobDescriptions.table.headers.status')
        },
        {
            key: "view",
            label: t('organizationalStructure.jobDescriptions.table.headers.view'),
            render: (item) => {
                if (!item.fileName) return '';
                const iconSrc = getFileIcon(item.fileName);
                if (!iconSrc) {
                    return '';
                }
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <img 
                            src={iconSrc}
                            alt=""
                            onClick={(e) => {
                                e.stopPropagation();
                                openJobDescriptionFile(item.id);
                            }}
                            style={{
                                width: '20px',
                                height: '20px',
                                cursor: 'pointer',
                                transition: 'opacity 0.2s ease',
                                display: 'block',
                                objectFit: 'contain'
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = '0.7'}
                            onMouseLeave={(e) => e.target.style.opacity = '1'}
                            onError={(e) => {
                                console.error('Failed to load file icon. fileName:', item.fileName, 'iconSrc:', iconSrc);
                                e.target.style.display = 'none';
                            }}
                           
                            title={`Click to view: ${item.fileName}`}
                        />
                    </div>
                );
            }
        }
    ]

    const searchOptions = [
        {
            key: "fileName",
            label: "fileName"
        }
    ]

    const data = jobDescriptions.map((jobDescription) => {
        return {
            id: jobDescription.id,
            fileName: jobDescription.fileName,
            description: jobDescription.description,
            status: jobDescription.active === 'true' ? t('users.table.search.options.active') : t('users.table.search.options.unactive'),
            view: jobDescription.contentType,
            active: jobDescription.active,
            empBranch: jobDescription.empBranchId
        };
    });


    const actionButtons = (
        <ActionButtons
                buttons={[
                    { ...COMMON_BUTTONS.ADD, onClick: () => setModalState({ visible: true, type: 'create', editData: null }) },
                    { ...COMMON_BUTTONS.EDIT, onClick: () => selectedRecord && handleOpenModal('edit') },
                    { ...COMMON_BUTTONS.DELETE, onClick: () => selectedRecord && handleOpenModal('delete') },
                ]}
                selectedRecord={selectedRecord}
            />
    );

    const handleOpenModal = (type) => {
        if (type === 'edit' && selectedRecord) {
            const selectedJobDescription = data.find(jd => jd.id === selectedRecord);
            setModalState({ visible: true, type, editData: selectedJobDescription });
        } else if (type === 'delete' && selectedRecord) {
            setModalState({ visible: true, type, editData: null });
        } else {
            setModalState({ visible: true, type, editData: null });
        }
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null, editData: null });
    };

    const handleCheckboxChange = (index) => {
        const jobDescription = data[index];
        setSelectedRecord(selectedRecord === jobDescription.id ? null : jobDescription.id);
    };

    const handleSearchClick = async (getDataFunc, params = {}) => {
        const fetchJobDescriptions = async () => {
            if (selectedCompany) {
                try {
                    setJobDescriptionsLoading(true);
                    setError(null);
                    
                    // Merge search query with active filter
                    const searchParams = { ...searchQuery };
                    if (selectedActive !== null && selectedActive !== '') {
                        searchParams.active = selectedActive === 'true';
                    }
                    
                    const response = await jobDescriptionsService.getJobDescriptions(selectedCompany, searchParams);
                    setJobDescriptions(response.content || []);
                } catch (error) {
                    console.error('❌ [handleSearchClick] Error fetching job descriptions:', error);
                    setJobDescriptions([]);
                    if (!handleJobDescriptionsAccessError(error)) {
                        setError('Failed to load job descriptions');
                    } else {
                        setError(null);
                    }
                } finally {
                    setJobDescriptionsLoading(false);
                }
            }
        };
        fetchJobDescriptions();
    };

    const handleConfirm = async (formData) => {
        try {
            // Helper function to refetch with active filter
            const refetchJobDescriptions = async () => {
                if (selectedCompany) {
                    const searchParams = {};
                    if (selectedActive !== null && selectedActive !== '') {
                        searchParams.active = selectedActive === 'true';
                    }
                    const response = await jobDescriptionsService.getJobDescriptions(selectedCompany, searchParams);
                    setJobDescriptions(response.content || []);
                }
            };

            if (modalState.type === 'edit') {
                await refetchJobDescriptions();
            } else if (modalState.type === 'delete') {
                await jobDescriptionsService.deleteJobDescription(selectedRecord);
                notifySuccess(t('common.deletedSuccessfully'));
                await refetchJobDescriptions();
                setSelectedRecord(null);
            } else {
                await refetchJobDescriptions();
            }
            handleCloseModal();
        } catch (error) {
            console.error('❌ [handleConfirm] Error in handleConfirm:', error);
            if (!handleJobDescriptionsAccessError(error)) {
                setError('Failed to save job description');
            } else {
                setError(null);
            }
        }
    };

    return (
        <>
            <Table
                title={t('organizationalStructure.jobDescriptions.title')}
                columns={columns}
                data={data}
                getData={() => { }}
                actionButtons={actionButtons}
                loading={jobDescriptionsLoading}
                error={error}
                selectedData={selectedRecord}
                handleCheckboxChange={handleCheckboxChange}
                showPageSizeSelector={false}
                showPagination={false}
                showPreviousPage={false}
                showCustomSearch={false}
                searchOptions={searchOptions}
                showActiveSelect={true}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                handleSearchClick={handleSearchClick}
                showCompanySelect={true}
                companyOptions={companies}
                selectedCompany={selectedCompany}
                setSelectedCompany={setSelectedCompany}
                selectedLevel={selectedLevel}
                setSelectedLevel={setSelectedLevel}
                selectedActive={selectedActive}
                setSelectedActive={setSelectedActive}
            />
            {modalState.visible && (modalState.type === 'create' || modalState.type === 'edit') && (
                <JobDescriptionForm
                    onClose={handleCloseModal}
                    onConfirm={handleConfirm}
                    editData={modalState.editData}
                    type={modalState.type}
                />
            )}
            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirm}
                    selectedData={selectedRecord}
                    name={data.find(jd => jd.id === selectedRecord)?.fileName}
                    entityType="record"
                />
            )}
        </>
    )
}
